/*
 * CRS BBS · 主页背景双端裁剪弹窗
 * 流程：选图 → 手机端裁剪(1.51:1) → 电脑端裁剪(8.23:1) → 一并上传
 * 裁剪框比例对应实际显示框实测值：手机 390x258、电脑 1440x175。
 * 预览框宽度流式（max 336px），几何尺寸运行时从 DOM 读取。
 */

import app from 'flarum/forum/app';
import Modal from 'flarum/components/Modal';
import Button from 'flarum/components/Button';
import LoadingIndicator from 'flarum/components/LoadingIndicator';

const CROPS = [
  { key: 'mobile', label: '手机端', aspect: 1.51, maxW: 1000, note: '显示框约 390×258' },
  { key: 'desktop', label: '电脑端', aspect: 8.23, maxW: 1600, note: '显示框约 1440×175（超宽横条）' },
];

const DEFAULT_BOX_W = 336;

export default class CoverCropModal extends Modal {
  oninit(vnode) {
    super.oninit(vnode);

    this.phase = 'pick'; // pick | crop | uploading
    this.cropIndex = 0;
    this.img = null;
    this.results = {};
    this.zoom = 1;
    this.minZoom = 1;
    this.ox = 0;
    this.oy = 0;
    this.dragging = false;
    this.error = '';
  }

  className() {
    return 'Modal--large CoverCropModal';
  }

  title() {
    return '上传主页背景（双端裁剪）';
  }

  content() {
    if (this.phase === 'pick') {
      return m(
        '.Modal-body',
        m('.CoverCrop-pick', [
          m('p.CoverCrop-hint', '选择一张图片，分别为手机端和电脑端裁剪显示范围。'),
          m(
            '.CoverCrop-pickbtn',
            m(
              Button,
              { className: 'Button Button--primary', icon: 'fas fa-image', onclick: () => this.openPicker() },
              '选择图片'
            )
          ),
          m('p.CoverCrop-hint2', '建议使用宽幅照片；两张裁剪会一次上传。'),
        ])
      );
    }

    if (this.phase === 'crop') {
      const c = CROPS[this.cropIndex];

      return m('.Modal-body', [
        m('p.CoverCrop-step', [
          `第 ${this.cropIndex + 1}/2 步 · `,
          m('strong', `${c.label}裁剪`),
          m('span.CoverCrop-note', `（${c.note}）`),
        ]),
        m(
          '.CoverCrop-box',
          {
            style: { width: '100%', maxWidth: DEFAULT_BOX_W + 'px', aspectRatio: String(c.aspect) },
            oncreate: (v) => {
              this._boxEl = v.dom;
              this.resetCrop();
            },
            onpointerdown: (e) => this.onDragStart(e),
            onpointermove: (e) => this.onDragMove(e),
            onpointerup: () => this.onDragEnd(),
            onpointercancel: () => this.onDragEnd(),
          },
          this.img
            ? m('img.CoverCrop-img', {
                src: this.img.src,
                draggable: false,
                style: {
                  transform: `translate(${this.ox}px, ${this.oy}px) scale(${this.zoom})`,
                },
              })
            : null
        ),
        m('.CoverCrop-zoomrow', [
          m('span', '缩放'),
          m('input.CoverCrop-zoom', {
            type: 'range',
            min: Math.round(this.minZoom * 100),
            max: Math.round(this.minZoom * 400),
            value: Math.round(this.zoom * 100),
            oninput: (e) => this.setZoom(Number(e.target.value) / 100),
          }),
        ]),
        this.error ? m('p.CoverCrop-error', this.error) : null,
        m('.CoverCrop-actions', [
          m(Button, { className: 'Button', onclick: () => this.backToPick() }, '重选图片'),
          m(
            Button,
            {
              className: 'Button Button--primary',
              icon: 'fas fa-check',
              onclick: () => this.finishCrop(),
            },
            this.cropIndex === 0 ? '下一步：电脑端' : '完成并上传'
          ),
        ]),
      ]);
    }

    // uploading
    return m(
      '.Modal-body.CoverCrop-uploading',
      m(LoadingIndicator, { size: 'large' }),
      m('p', '正在上传…')
    );
  }

  /* ---------- 盒子几何 ---------- */

  getBoxW() {
    return this._boxEl && this._boxEl.clientWidth ? this._boxEl.clientWidth : DEFAULT_BOX_W;
  }

  getBoxH() {
    return this.getBoxW() / CROPS[this.cropIndex].aspect;
  }

  /* ---------- 选图 ---------- */

  openPicker() {
    const input = document.createElement('input');
    input.type = 'file';
    input.accept = 'image/jpeg,image/png,image/webp';
    input.style.display = 'none';
    document.body.appendChild(input);
    input.addEventListener('change', () => {
      const file = input.files && input.files[0];
      input.remove();
      if (file) this.loadFile(file);
    });
    input.click();
  }

  loadFile(file) {
    if (file.size > 15 * 1024 * 1024) {
      this.error = '图片太大（原始图请小于 15MB）';
      m.redraw();
      return;
    }

    const reader = new FileReader();
    reader.onload = () => {
      const img = new Image();
      img.onload = () => {
        this.img = img;
        this.error = '';
        this.results = {};
        this.cropIndex = 0;
        this.phase = 'crop';
        // resetCrop 在 box oncreate 后也会触发；这里先算一次兜底
        this.resetCrop();
        m.redraw();
      };
      img.onerror = () => {
        this.error = '图片读取失败，请换一张试试';
        m.redraw();
      };
      img.src = reader.result;
    };
    reader.readAsDataURL(file);
  }

  /* ---------- 裁剪几何 ---------- */

  resetCrop() {
    if (!this.img) return;
    // cover-fit：铺满裁剪框的最小缩放
    this.minZoom = Math.max(this.getBoxW() / this.img.naturalWidth, this.getBoxH() / this.img.naturalHeight);
    this.zoom = this.minZoom;
    this.ox = 0;
    this.oy = 0;
    this.clampPan();
  }

  setZoom(z) {
    this.zoom = Math.min(Math.max(z, this.minZoom), this.minZoom * 4);
    this.clampPan();
  }

  clampPan() {
    if (!this.img) return;
    const dw = this.img.naturalWidth * this.zoom;
    const dh = this.img.naturalHeight * this.zoom;
    const bw = this.getBoxW();
    const bh = this.getBoxH();

    const minX = Math.min(0, bw - dw);
    const minY = Math.min(0, bh - dh);
    const maxX = Math.max(0, bw - dw);
    const maxY = Math.max(0, bh - dh);

    this.ox = Math.min(Math.max(this.ox, minX), maxX);
    this.oy = Math.min(Math.max(this.oy, minY), maxY);
  }

  onDragStart(e) {
    if (this.phase !== 'crop') return;
    if (e.currentTarget.setPointerCapture) e.currentTarget.setPointerCapture(e.pointerId);
    this.dragging = true;
    this._lx = e.clientX;
    this._ly = e.clientY;
  }

  onDragMove(e) {
    if (!this.dragging) return;
    const dx = e.clientX - this._lx;
    const dy = e.clientY - this._ly;
    this._lx = e.clientX;
    this._ly = e.clientY;
    this.ox += dx;
    this.oy += dy;
    this.clampPan();
    m.redraw();
  }

  onDragEnd() {
    this.dragging = false;
  }

  /* ---------- 导出与上传 ---------- */

  finishCrop() {
    const c = CROPS[this.cropIndex];
    const bw = this.getBoxW();
    const bh = this.getBoxH();

    // 预览框在原图坐标系里的矩形（translate+scale 逆变换）
    const sx = -this.ox / this.zoom;
    const sy = -this.oy / this.zoom;
    const sw = bw / this.zoom;
    const sh = bh / this.zoom;

    // 导出尺寸：不超过 maxW，且不放大小于 maxW 的源区域
    const outW = Math.min(c.maxW, Math.round(sw));
    const outH = Math.round(outW / c.aspect);

    const canvas = document.createElement('canvas');
    canvas.width = outW;
    canvas.height = outH;
    const ctx = canvas.getContext('2d');
    ctx.drawImage(this.img, sx, sy, sw, sh, 0, 0, outW, outH);

    try {
      this.results[c.key] = canvas.toDataURL('image/jpeg', 0.9);
    } catch (e) {
      this.error = '导出图片失败：' + e.message;
      m.redraw();
      return;
    }

    if (this.cropIndex < CROPS.length - 1) {
      this.cropIndex++;
      this.resetCrop();
      m.redraw();
    } else {
      this.upload();
    }
  }

  upload() {
    this.phase = 'uploading';
    this.error = '';
    m.redraw();

    app
      .request({
        method: 'POST',
        url: `${app.forum.attribute('apiUrl')}/users/${this.attrs.user.id()}/cover-crops`,
        body: {
          data: {
            attributes: {
              desktopCropped: this.results.desktop,
              mobileCropped: this.results.mobile,
            },
          },
        },
      })
      .then(
        (response) => {
          app.store.pushPayload(response);
          app.alerts.show({ type: 'success' }, '主页背景已更新（双端）');
          this.hide();
        },
        () => {
          this.phase = 'crop';
          this.cropIndex = CROPS.length - 1;
          this.error = '上传失败，请重试';
          m.redraw();
        }
      );
  }

  backToPick() {
    this.phase = 'pick';
    this.error = '';
    m.redraw();
  }
}
